import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";

import type { DocsList, Pendientes } from "./types";
import { userManager } from "../hooks/OidcConfig";
import { RootState } from "../store";

const baseUrl = import.meta.env.VITE_URL_BACK;

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    console.error("El backend rechazó el token (401). Expulsando al usuario...");
    try {
      const user = await userManager.signinSilent();

      if (user?.access_token) {
        result = await baseQuery(args, api, extraOptions);
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error("No se pudo renovar el token. Sesión terminada.");
      await userManager.removeUser();
      window.location.reload();
    }
  }

  return result;
};
const baseQuery = fetchBaseQuery({
  baseUrl: baseUrl,
  prepareHeaders: async (headers, { getState }) => {
    const state = getState() as RootState;
    const token = state.ui.token;

    if (token) {
      headers.set(`Authorization`, `Bearer ${token}`);
    }

    return headers;
  },
});
export const docsApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    cargaDocumento: builder.mutation({
      query: (post) => ({
        body: post,
        invalidatesTags: ["cargaDocumento"],
        method: "post",
        url: "/siniestro/carga-documento",
      }),
    }),
    getValidacion: builder.mutation({
      query: (post) => ({
        body: post,
        invalidatesTags: ["estatus-siniestro"],
        method: "post",
        url: "/cop/enviar-validacion",
      }),
    }),
    Dictamen: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/cop/dictamen",
      }),
    }),
    enviarAseguradora: builder.mutation({
      query: (post) => ({
        body: post,
        invalidatesTags: ["estatus-siniestro"],
        method: "put",
        url: "/cop/enviar-aseguradora",
      }),
    }),
    auditoria: builder.mutation({
      query: (post) => ({
        body: post,
        invalidatesTags: ["estatus-siniestro"],
        method: "post",
        url: "/cop/auditoria",
      }),
    }),
    calificarDocumento: builder.mutation({
      query: (post) => ({
        body: post,
        invalidatesTags: ["cargaDocumento"],
        method: "put",
        url: "/cop/guardar-calificacion",
      }),
    }),
    finalizarValidacion: builder.mutation({
      query: (post) => ({
        body: post,
        invalidatesTags: ["cargaDocumento"],
        method: "put",
        url: "/cop/finalizar-validacion",
      }),
    }),
    getDocsList: builder.query<DocsList, any>({
      providesTags: (result, error, arg) => [
        { type: "cargaDocumento", id: "LIST" },
      ],
      query: (info: any) =>
        `/siniestro/documentos/${info.siniestro}/${info.tramite}`,
    }),
    getPendientes: builder.query<Pendientes, void>({
      keepUnusedDataFor: 0,
      query: () => `/cop/mis-pendientes`,
    }),
    getProcesoActual: builder.query<any, any>({
      keepUnusedDataFor: 0,
      providesTags: ["estatus-siniestro"],
      query: (info: any) =>
        `/cop/proceso-actual/${info.siniestro}/${info.tramite}`,
    }),
    getCallcenter: builder.query<any, any>({
      keepUnusedDataFor: 0,
      query: (info: any) => `/cop/callcenter/${info.siniestro}/${info.tramite}`,
    }),
    saveCallcenter: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/cop/callcenter",
      }),
    }),
    getFinalizado: builder.query<any, any>({
      keepUnusedDataFor: 0,
      query: (info: any) => `/cop/finalizado/${info.siniestro}/${info.tramite}`,
    }),
    catRespuestas: builder.query<any, any>({
      keepUnusedDataFor: 0,
      query: (info: any) =>
        `/cop/cat-respuesta/${info.siniestro}/${info.tramite}`,
    }),
    getDictamenes: builder.query<any, any>({
      keepUnusedDataFor: 0,
      query: (info: any) => `/cop/dictamenes/${info.siniestro}/${info.tramite}`,
    }),
    getSemaforo: builder.query<any, any>({
      keepUnusedDataFor: 0,
      query: (info: any) => `/cop/tiempos/${info.siniestro}/${info.tramite}`,
    }),
    getBusqueda: builder.query<any, void>({
      keepUnusedDataFor: 0,
      query: () => `/cop/buscar`,
    }),
    getReportes: builder.query<any, void>({
      keepUnusedDataFor: 0,
      query: () => `/cop/reportes`,
    }),
    buscarTramite: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/cop/buscar",
      }),
    }),
    generarReporte: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/cop/reportes",
      }),
    }),
    getSiniestro: builder.query<any, any>({
      query: (siniestro: any) => `/cop/informacion-siniestro/${siniestro}`,
    }),
    getEtapasTramite: builder.query<any, any>({
      query: (info: any) =>
        `/siniestro/etapas/${info.siniestro}/${info.tramite}`,
    }),
    login: builder.mutation({
      query: (post) => ({
        body: post,
        invalidatesTags: ["sesion"],
        method: "post",
        url: "/auth/login-wallet",
      }),
    }),
    validarSesion: builder.query<any, void>({
      providesTags: ["sesion"],
      query: () => "/auth/check-status",
    }),
    getNotificaciones: builder.query<any, any>({
      keepUnusedDataFor: 0,
      query: (info: any) =>
        `/cop/notificaciones/${info.siniestro}/${info.tramite}`,
    }),
    documentoAdicional: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/cop/documento-adicional",
      }),
    }),
    getDocumentosAdicionales: builder.query<any, any>({
      keepUnusedDataFor: 0,
      query: (info: any) =>
        `/cop/documentos-adicionales/${info.siniestro}/${info.tramite}`,
    }),
    eliminardocAdi: builder.mutation({
      query: (post) => ({
        body: post,
        method: "put",
        url: "/cop/documento-adicional",
      }),
    }),
    buscarPoliza: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/siniestro/buscar-poliza",
      }),
    }),
    nuevoTramite: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/siniestro/nuevo-tramite",
      }),
    }),
    datosTramite: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/siniestro/datos-tramite",
      }),
    }),
    editarDocumento: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/siniestro/documento-tramite",
      }),
    }),
    getReportesEmision: builder.query<any, void>({
      query: () => `/emisiones/reportes`,
    }),
    getPendientesFull: builder.query<any, void>({
      query: () => `/emisiones/pendientes-full`,
    }),
    getValidaciones: builder.query<any, void>({
      query: () => `/cop/validaciones`,
    }),
    generarReporteEmision: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/emisiones/reporte-emision",
      }),
    }),
    getTipoConsultaEmision: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/emisiones/busqueda",
      }),
    }),
    ConsultaEmision: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/emisiones/busqueda-emision",
      }),
    }),
    VerEmision: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/emisiones/ver-emision",
      }),
    }),
    VerEmisionDetalle: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/emisiones/ver-emisiones-detalle",
      }),
    }),

    getPoliza: builder.query<any, any>({
      query: (poliza_id: any) => `/siniestro/buscar-poliza/${poliza_id}`,
    }),
    buscarEspecialista: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/cop/buscar-especialista",
      }),
    }),
    eliminarTramite: builder.mutation({
      query: (post) => ({
        body: post,
        method: "put",
        url: "/cop/eliminar-tramite",
      }),
    }),
    agregarEspecialista: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/cop/agregar-especialista",
      }),
    }),
    getTramitesSinAsignar: builder.query<any, void>({
      query: () => `/cop/tramites-sin-asignar`,
    }),
    asignarTramites: builder.mutation({
      query: (post) => ({
        body: post,
        method: "put",
        url: "/cop/asignar-tramites",
      }),
    }),
    getEspecialistas: builder.query<any, void>({
      providesTags: ["especialistas"],
      query: () => `/cop/especialistas`,
    }),
    rmEspecialista: builder.mutation({
      query: (post) => ({
        body: post,
        method: "put",
        url: "/cop/especialistas",
      }),
      invalidatesTags: ["especialistas"],
    }),
    cargarCondiciones: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/siniestro/carga-condiciones",
      }),
    }),
    cargarArchivoBucket: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/siniestro/carga-archivo-bucket",
      }),
    }),
    cargarFormato: builder.mutation({
      query: (post) => ({
        body: post,
        method: "put",
        url: "/cop/formato-reclamacion",
      }),
    }),
    getWakaway: builder.query<any, void>({
      query: () => `/cop/wakaway`,
    }),
    getEnviarWalkaway: builder.mutation({
      query: (post) => ({
        body: post,
        invalidatesTags: ["estatus-siniestro"],
        method: "post",
        url: "/cop/enviar-walkaway",
      }),
    }),
    getPermisos: builder.query<any, void>({
      query: () => `/auth/permisos`,
    }),
    postAvatar: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/cop/perfil",
      }),
    }),
    agregarDocumento: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/cop/documento-adicional-wallet",
      }),
    }),
    getBuscarProducto: builder.query<any, void>({
      keepUnusedDataFor: 0,
      query: () => `/cop/buscar-producto`,
    }),
    cargarDocumentoGenerico: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/siniestro/carga-documento-generico",
      }),
    }),
    reenviarSFTP: builder.mutation<any, any>({
      invalidatesTags: [{ type: "cargaDocumento", id: "LIST" }],
      query: (info: any) =>
        `/cop/archivo-ftp/${info.siniestro}/${info.tramite}`,
    }),
  }),
  reducerPath: "api",
  tagTypes: ["sesion", "cargaDocumento", "estatus-siniestro", "especialistas"],
});

export const {
  useGetDocsListQuery,
  useValidarSesionQuery,
  useLazyValidarSesionQuery,
  useLoginMutation,
  useGetPendientesQuery,
  useGetSiniestroQuery,
  useGetProcesoActualQuery,
  useCargaDocumentoMutation,
  useLazyGetDocsListQuery,
  useLazyGetPendientesQuery,
  useGetValidacionMutation,
  useCalificarDocumentoMutation,
  useFinalizarValidacionMutation,
  useEnviarAseguradoraMutation,
  useCatRespuestasQuery,
  useDictamenMutation,
  useAuditoriaMutation,
  useGetCallcenterQuery,
  useSaveCallcenterMutation,
  useGetFinalizadoQuery,
  useGetDictamenesQuery,
  useGetSemaforoQuery,
  useGetBusquedaQuery,
  useBuscarTramiteMutation,
  useGetNotificacionesQuery,
  useDocumentoAdicionalMutation,
  useLazyGetDocumentosAdicionalesQuery,
  useEliminardocAdiMutation,
  useGetEtapasTramiteQuery,
  useBuscarPolizaMutation,
  useNuevoTramiteMutation,
  useDatosTramiteMutation,
  useEditarDocumentoMutation,
  useGetReportesEmisionQuery,
  useGenerarReporteEmisionMutation,
  useGetTipoConsultaEmisionMutation,
  useConsultaEmisionMutation,
  useLazyGetPendientesFullQuery,
  useGetPolizaQuery,
  useGetValidacionesQuery,
  useBuscarEspecialistaMutation,
  useAgregarEspecialistaMutation,
  useGetTramitesSinAsignarQuery,
  useAsignarTramitesMutation,
  useLazyGetTramitesSinAsignarQuery,
  useGetEspecialistasQuery,
  useLazyGetEspecialistasQuery,
  useRmEspecialistaMutation,
  useCargarCondicionesMutation,
  useGetReportesQuery,
  useGenerarReporteMutation,
  useEliminarTramiteMutation,
  useCargarFormatoMutation,
  useGetWakawayQuery,
  useGetEnviarWalkawayMutation,
  useVerEmisionMutation,
  useVerEmisionDetalleMutation,
  useCargarArchivoBucketMutation,
  useGetPermisosQuery,
  usePostAvatarMutation,
  useAgregarDocumentoMutation,
  useGetBuscarProductoQuery,
  useCargarDocumentoGenericoMutation,
  useLazyGetPermisosQuery,
  useReenviarSFTPMutation,
} = docsApi;
